import businessMetricsService from './businessMetricsService';

/**
 * Service to fetch all available data headers from various sources
 * to populate the dynamic Component Library
 */

export interface DataHeader {
  id: string;
  name: string;
  displayName: string;
  source: 'business_metric' | 'csv_import' | 'monthly_trend' | 'cloud_cost' | 'custom_cost';
  dataType: 'number' | 'currency' | 'percentage' | 'text' | 'date';
  unit?: string;
  metricType?: string;
  description?: string;
  sampleValue?: any;
}

export interface DataHeaderCategory {
  category: string;
  displayName: string;
  description: string;
  headers: DataHeader[];
}

class DataHeaderService {
  /**
   * Fetch all available data headers from all sources
   * If month is not specified, fetches data from all months
   */
  async getAllHeaders(month?: string, includeAllMonths: boolean = false): Promise<DataHeaderCategory[]> {
    try {
      const categories: DataHeaderCategory[] = [];

      // 1. Fetch Business Metrics Headers
      const businessMetricsHeaders = await this.getBusinessMetricsHeaders(month, includeAllMonths);
      if (businessMetricsHeaders.length > 0) {
        categories.push({
          category: 'business_metrics',
          displayName: 'Business Metrics',
          description: includeAllMonths
            ? 'Metrics from all months (manual entries and CSV imports)'
            : `Metrics from ${month || 'current month'} (manual entries and CSV imports)`,
          headers: businessMetricsHeaders
        });
      }

      // 2. Add Cloud Cost Headers
      const cloudCostHeaders = this.getCloudCostHeaders();
      categories.push({
        category: 'cloud_costs',
        displayName: 'Cloud Costs',
        description: 'AWS, GCP, and GitHub infrastructure costs',
        headers: cloudCostHeaders
      });

      // 3. Add Custom Cost Headers
      const customCostHeaders = this.getCustomCostHeaders();
      categories.push({
        category: 'custom_costs',
        displayName: 'Custom Costs',
        description: 'SaaS subscriptions and vendor costs',
        headers: customCostHeaders
      });

      // 4. Add Derived Metrics (from existing calculations)
      const derivedHeaders = this.getDerivedMetricHeaders();
      categories.push({
        category: 'derived_metrics',
        displayName: 'Calculated Metrics',
        description: 'Previously calculated unit economics metrics',
        headers: derivedHeaders
      });

      return categories;
    } catch (error) {
      console.error('Error fetching data headers:', error);
      return [];
    }
  }

  /**
   * Fetch business metrics headers from the backend
   * If includeAllMonths is true, fetches from all available months
   */
  private async getBusinessMetricsHeaders(month?: string, includeAllMonths: boolean = false): Promise<DataHeader[]> {
    try {
      let metrics;

      if (includeAllMonths) {
        // Fetch all metrics from all months
        metrics = await businessMetricsService.getMetrics({});
      } else {
        // Fetch metrics for specific month
        metrics = await businessMetricsService.getMetrics({ month });
      }

      // Create a header for each unique metric (by metricName + metricType + month combination)
      const headerMap = new Map<string, DataHeader>();

      // Track CSV column aggregations (sum all rows for each column)
      const csvColumnAggregations = new Map<string, { sum: number; count: number; unit: string; metricType: string; month: string }>();

      metrics.forEach(metric => {
        // Include month in the ID when fetching all months so metrics from different months are distinct
        const monthSuffix = includeAllMonths ? `_${metric.month}` : '';
        const headerId = `metric_${metric.metricType}_${metric.metricName.replace(/\s+/g, '_')}${monthSuffix}`;

        if (!headerMap.has(headerId)) {
          const displayName = includeAllMonths
            ? `${metric.metricName} (${metric.month})`
            : metric.metricName;

          headerMap.set(headerId, {
            id: headerId,
            name: metric.metricName, // Use the actual metric name (e.g., "Total Customers", "Monthly Revenue")
            displayName,
            source: metric.source === 'csv_import' ? 'csv_import' : 'business_metric',
            dataType: this.inferDataType(metric.metricType),
            unit: metric.unit,
            metricType: metric.metricType,
            description: `${metric.metricType.replace('_', ' ')}: ${metric.metricName} (${metric.source}, ${metric.month})`,
            sampleValue: metric.value
          });
        }

        // For CSV imports with format "Row - Column", aggregate by column
        if (metric.source === 'csv_import' && metric.metricName.includes(' - ')) {
          const csvColumn = metric.metricName.split(' - ').slice(1).join(' - ');
          const aggregationKey = includeAllMonths ? `${csvColumn}_${metric.month}` : csvColumn;

          if (!csvColumnAggregations.has(aggregationKey)) {
            csvColumnAggregations.set(aggregationKey, {
              sum: 0,
              count: 0,
              unit: metric.unit,
              metricType: metric.metricType,
              month: metric.month
            });
          }

          const agg = csvColumnAggregations.get(aggregationKey)!;
          agg.sum += metric.value;
          agg.count += 1;
        }
      });

      // Add aggregated headers for CSV columns
      csvColumnAggregations.forEach((agg, key) => {
        const isMultiMonth = includeAllMonths && key.includes('_');
        let columnName, monthPart;

        if (isMultiMonth) {
          const lastUnderscoreIndex = key.lastIndexOf('_');
          columnName = key.substring(0, lastUnderscoreIndex);
          monthPart = key.substring(lastUnderscoreIndex + 1);
        } else {
          columnName = key;
          monthPart = '';
        }

        const displayName = isMultiMonth
          ? `${columnName} (Total, ${monthPart})`
          : `${columnName} (Total)`;

        const headerId = `metric_aggregated_${key.replace(/\s+/g, '_')}`;

        headerMap.set(headerId, {
          id: headerId,
          name: `${columnName}_total`,
          displayName,
          source: 'csv_import',
          dataType: this.inferDataType(agg.metricType),
          unit: agg.unit,
          metricType: agg.metricType,
          description: `Sum of all rows for ${columnName} (${agg.count} items, ${agg.month})`,
          sampleValue: agg.sum
        });
      });

      return Array.from(headerMap.values());
    } catch (error) {
      console.error('Error fetching business metrics headers:', error);
      return [];
    }
  }

  /**
   * Get cloud cost headers
   */
  private getCloudCostHeaders(): DataHeader[] {
    return [
      {
        id: 'cloud_cost_total',
        name: 'total_cloud_cost',
        displayName: 'Total Cloud Cost',
        source: 'cloud_cost',
        dataType: 'currency',
        unit: 'USD',
        description: 'Combined AWS + GCP + GitHub costs'
      },
      {
        id: 'cloud_cost_aws',
        name: 'aws_cost',
        displayName: 'AWS Cost',
        source: 'cloud_cost',
        dataType: 'currency',
        unit: 'USD',
        description: 'Amazon Web Services costs'
      },
      {
        id: 'cloud_cost_gcp',
        name: 'gcp_cost',
        displayName: 'GCP Cost',
        source: 'cloud_cost',
        dataType: 'currency',
        unit: 'USD',
        description: 'Google Cloud Platform costs'
      },
      {
        id: 'cloud_cost_github',
        name: 'github_cost',
        displayName: 'GitHub Cost',
        source: 'cloud_cost',
        dataType: 'currency',
        unit: 'USD',
        description: 'GitHub Actions and storage costs'
      }
    ];
  }

  /**
   * Get custom cost headers
   */
  private getCustomCostHeaders(): DataHeader[] {
    return [
      {
        id: 'custom_cost_total',
        name: 'total_custom_cost',
        displayName: 'Total Custom Cost',
        source: 'custom_cost',
        dataType: 'currency',
        unit: 'USD',
        description: 'Total SaaS and vendor costs'
      },
      {
        id: 'custom_cost_saas',
        name: 'saas_cost',
        displayName: 'SaaS Subscriptions',
        source: 'custom_cost',
        dataType: 'currency',
        unit: 'USD',
        description: 'Software-as-a-Service subscriptions'
      },
      {
        id: 'custom_cost_vendor',
        name: 'vendor_cost',
        displayName: 'Vendor Costs',
        source: 'custom_cost',
        dataType: 'currency',
        unit: 'USD',
        description: 'Third-party vendor costs'
      }
    ];
  }

  /**
   * Get derived metric headers (common calculations)
   */
  private getDerivedMetricHeaders(): DataHeader[] {
    return [
      {
        id: 'derived_cost_per_customer',
        name: 'cost_per_customer',
        displayName: 'Cost per Customer',
        source: 'business_metric',
        dataType: 'currency',
        unit: 'USD/customer',
        description: 'Total cost divided by customer count'
      },
      {
        id: 'derived_revenue_per_cost',
        name: 'revenue_per_cost',
        displayName: 'Revenue per Dollar',
        source: 'business_metric',
        dataType: 'number',
        unit: 'ratio',
        description: 'Revenue divided by total cost'
      },
      {
        id: 'derived_gross_margin',
        name: 'gross_margin',
        displayName: 'Gross Margin',
        source: 'business_metric',
        dataType: 'currency',
        unit: 'USD',
        description: 'Revenue minus total costs'
      },
      {
        id: 'derived_margin_percentage',
        name: 'margin_percentage',
        displayName: 'Margin %',
        source: 'business_metric',
        dataType: 'percentage',
        unit: '%',
        description: 'Gross margin as percentage of revenue'
      }
    ];
  }

  /**
   * Infer data type from metric type
   */
  private inferDataType(metricType: string): 'number' | 'currency' | 'percentage' | 'text' | 'date' {
    if (metricType.includes('revenue') || metricType.includes('arr') || metricType.includes('mrr')) {
      return 'currency';
    }
    if (metricType.includes('percentage') || metricType.includes('rate')) {
      return 'percentage';
    }
    if (metricType.includes('count') || metricType.includes('transactions') || metricType.includes('usage')) {
      return 'number';
    }
    return 'number';
  }

  /**
   * Get headers for a specific category
   */
  async getHeadersByCategory(category: string, month?: string): Promise<DataHeader[]> {
    const allCategories = await this.getAllHeaders(month);
    const found = allCategories.find(c => c.category === category);
    return found ? found.headers : [];
  }

  /**
   * Search headers by name or description
   */
  async searchHeaders(query: string, month?: string): Promise<DataHeader[]> {
    const allCategories = await this.getAllHeaders(month);
    const allHeaders = allCategories.flatMap(c => c.headers);

    const lowerQuery = query.toLowerCase();
    return allHeaders.filter(header =>
      header.displayName.toLowerCase().includes(lowerQuery) ||
      header.name.toLowerCase().includes(lowerQuery) ||
      header.description?.toLowerCase().includes(lowerQuery)
    );
  }
}

export default new DataHeaderService();
